import api from './api';

// API service for wallet operations
export const walletApi = {
  // Generate a new wallet
  async generateWallet(name = null, tokenId = null) {
    console.log('walletApi.generateWallet: Generating new wallet, tokenId:', tokenId);
    try {
      // Use the working wallets/generate route directly
      const response = await api.post('/wallets/generate', { name, tokenId });
      console.log('walletApi.generateWallet: Response from /wallets/generate:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.generateWallet ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to generate wallet');
    }
  },
  
  // Generate multiple wallets
  async generateMultipleWallets(count = 1, namePrefix = 'Wallet', groupId = null, createNewGroup = false, newGroupName = '', tokenId = null) {
    console.log(`walletApi.generateMultipleWallets: Generating ${count} wallets, tokenId: ${tokenId}`);
    try {
      const response = await api.post('/wallets/generate-bulk', { 
        count, 
        namePrefix,
        groupId,
        createNewGroup,
        newGroupName,
        tokenId
      });
      console.log(`walletApi.generateMultipleWallets: Generated ${response.data.wallets?.length || response.data.count} wallets`);
      return response.data;
    } catch (error) {
      console.error('walletApi.generateMultipleWallets ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to generate wallets');
    }
  },
  
  // Get all user wallets with optional group and token filtering
  async getUserWallets(groupId = null, tokenId = null) {
    console.log(`🌐 [walletApi.getUserWallets] DEBUGGING: Fetching wallets${groupId ? ` for group ${groupId}` : ''}${tokenId ? ` for token ${tokenId}` : ''}`);
    console.log('🌐 [walletApi.getUserWallets] Parameters received:', { groupId, tokenId });
    try {
      // Build the URL with query parameters
      let url = '/wallets';
      const params = [];
      if (groupId !== null && groupId !== undefined) {
        params.push(`groupId=${encodeURIComponent(groupId)}`);
      }
      if (tokenId !== null && tokenId !== undefined) {
        params.push(`tokenId=${encodeURIComponent(tokenId)}`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      console.log(`🌐 [walletApi.getUserWallets] Final URL: ${url}`);
      const response = await api.get(url);
      console.log(`🌐 [walletApi.getUserWallets] API Response:`, response.data);
      console.log(`🌐 [walletApi.getUserWallets] Wallet count in response:`, response.data?.wallets?.length || 0);
      
      // Handle both old format (array) and new format (object with wallets/groups)
      let wallets, groups;
      if (Array.isArray(response.data)) {
        // Old format - just an array of wallets
        wallets = response.data;
        groups = [];
      } else {
        // New format - object with wallets and groups
        wallets = response.data.wallets || [];
        groups = response.data.groups || [];
      }
      
      console.log(`walletApi.getUserWallets: Processed ${wallets.length} wallets and ${groups.length} groups`);
      
      // Cache the wallets for offline use
      this.cacheWallets(wallets);
      
      return {
        wallets,
        groups
      };
    } catch (error) {
      console.error('walletApi.getUserWallets ERROR:', error);
      
      // If server is down, return cached wallets
      if (error.isServerDown) {
        const cachedWallets = JSON.parse(localStorage.getItem('cachedWallets') || '[]');
        console.log('Server appears to be down, returning cached wallets:', cachedWallets.length);
        return {
          wallets: cachedWallets,
          groups: []
        };
      }
      
      throw new Error(error.response?.data?.error || 'Failed to fetch wallets');
    }
  },

  // Get ungrouped wallets
  async getUngroupedWallets(tokenId = null) {
    console.log('walletApi.getUngroupedWallets: Fetching ungrouped wallets, tokenId:', tokenId);
    try {
      let url = '/wallets/ungrouped';
      if (tokenId) {
        url += `?tokenId=${encodeURIComponent(tokenId)}`;
      }
      const response = await api.get(url);
      console.log(`walletApi.getUngroupedWallets: Received ${response.data.wallets?.length || 0} wallets`);
      return response.data;
    } catch (error) {
      console.error('walletApi.getUngroupedWallets ERROR:', error);
      
      // If server is down, return cached wallets
      if (error.isServerDown) {
        const cachedWallets = JSON.parse(localStorage.getItem('cachedWallets') || '[]');
        console.log('Server appears to be down, returning cached wallets:', cachedWallets.length);
        return {
          wallets: cachedWallets,
          groups: []
        };
      }
      
      throw new Error(error.response?.data?.error || 'Failed to fetch ungrouped wallets');
    }
  },
  
  // Get a specific wallet by ID
  async getWalletById(walletId) {
    console.log('walletApi.getWalletById: Fetching wallet:', walletId);
    try {
      const response = await api.get(`/wallets/${walletId}`);
      console.log('walletApi.getWalletById: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.getWalletById ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch wallet');
    }
  },
  
  // Update a wallet's name or default status
  async updateWallet(walletId, updateData) {
    console.log('walletApi.updateWallet: Updating wallet:', walletId);
    try {
      const response = await api.put(`/wallets/${walletId}`, updateData);
      console.log('walletApi.updateWallet: Response:', response.data);
      
      // Update cached wallets
      const cachedWallets = JSON.parse(localStorage.getItem('cachedWallets') || '[]');
      const updatedWallets = cachedWallets.map(wallet => 
        wallet.id === walletId ? { ...wallet, ...updateData } : wallet
      );
      localStorage.setItem('cachedWallets', JSON.stringify(updatedWallets));
      
      return response.data;
    } catch (error) {
      console.error('walletApi.updateWallet ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to update wallet');
    }
  },
  
  // Delete a wallet
  async deleteWallet(walletId) {
    console.log('walletApi.deleteWallet: Deleting wallet:', walletId);
    try {
      const response = await api.delete(`/wallets/${walletId}`);
      console.log('walletApi.deleteWallet: Response:', response.data);
      
      // Update cached wallets
      const cachedWallets = JSON.parse(localStorage.getItem('cachedWallets') || '[]');
      const updatedWallets = cachedWallets.filter(wallet => wallet.id !== walletId);
      localStorage.setItem('cachedWallets', JSON.stringify(updatedWallets));
      
      return response.data;
    } catch (error) {
      console.error('walletApi.deleteWallet ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete wallet');
    }
  },
  
async exportWalletsToExcel(groupId = null) {
  console.log(`walletApi.exportWalletsToExcel: Exporting wallets to Excel${groupId ? ` for group ${groupId}` : ''}`);
  try {
    // Build URL with group parameter if provided
    let url = '/wallets/export';
    if (groupId !== null && groupId !== undefined) {
      url += `?groupId=${encodeURIComponent(groupId)}`;
    }
    
    // Use blob response type for file download
    const response = await api.get(url, { responseType: 'blob' });
    
    // Create filename with .xlsx extension
    const filename = `wallets${groupId ? `_group_${groupId}` : '_all'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    
    // Download file as Excel
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log('walletApi.exportWalletsToExcel: Successfully downloaded wallets');
    return true;
  } catch (error) {
    console.error('walletApi.exportWalletsToExcel ERROR:', error);
    throw new Error('Failed to export wallets to Excel');
  }
},
  
  // Export all wallets to CSV
  async exportAllWallets() {
    console.log('walletApi.exportAllWallets: Exporting all wallets');
    try {
      // Use blob response type for file download
      const response = await api.get('/wallets/export/all', { 
        responseType: 'blob'
      });
      
      // Create filename
      const filename = `all_wallets_${new Date().toISOString().slice(0, 10)}.csv`;
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('walletApi.exportAllWallets: Successfully downloaded all wallets');
      return true;
    } catch (error) {
      console.error('walletApi.exportAllWallets ERROR:', error);
      
      // Display a more useful error message to the user
      if (error.response && error.response.status === 404) {
        throw new Error('No wallets found to export');
      }
      
      throw new Error('Failed to export wallets: ' + (error.message || 'Unknown error'));
    }
  },
  
  // Get wallets with private keys for chatbot display
  async getWalletsForChatbot(tokenId = null) {
    console.log('walletApi.getWalletsForChatbot: Fetching wallet data for chatbot, tokenId:', tokenId);
    try {
      // Clear any cached wallet data to ensure fresh fetch
      localStorage.removeItem('cachedWallets');
      
      // Force cache bust by adding timestamp and include tokenId if provided
      let url = `/wallets/chatbot-data?_t=${Date.now()}`;
      if (tokenId) {
        url += `&tokenId=${encodeURIComponent(tokenId)}`;
      }
      const response = await api.get(url);
      console.log(`walletApi.getWalletsForChatbot: Received ${response.data.length} wallets${tokenId ? ` for token ${tokenId}` : ''}`);
      
      // Log first wallet's private key format for debugging
      if (response.data.length > 0) {
        const firstWallet = response.data[0];
        console.log('Sample wallet private key format:', {
          length: firstWallet.private_key?.length,
          startsWithBase58: /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]/.test(firstWallet.private_key || ''),
          isBase64Like: /^[A-Za-z0-9+/]+=*$/.test(firstWallet.private_key || ''),
          preview: firstWallet.private_key?.substring(0, 20) + '...'
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('walletApi.getWalletsForChatbot ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch wallet data');
    }
  },

  // Get wallet private key - REMOVED FOR SECURITY
  // Private keys are no longer accessible through the frontend
  async getWalletPrivateKey(address) {
    throw new Error('Private key access has been disabled for security reasons');
  },

  // Create a wallet group
  async createWalletGroup(groupData, tokenId = null) {
    console.log('walletApi.createWalletGroup: Creating wallet group:', groupData, 'tokenId:', tokenId);
    try {
      const dataWithToken = tokenId ? { ...groupData, token_id: tokenId } : groupData;
      const response = await api.post('/wallet-groups', dataWithToken);
      console.log('walletApi.createWalletGroup: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.createWalletGroup ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to create wallet group');
    }
  },

  // Get wallet groups
  async getWalletGroups(tokenId = null) {
    console.log('walletApi.getWalletGroups: Fetching wallet groups, tokenId:', tokenId);
    try {
      let url = '/wallet-groups';
      if (tokenId) {
        url += `?tokenId=${encodeURIComponent(tokenId)}`;
      }
      const response = await api.get(url);
      console.log(`walletApi.getWalletGroups: Received ${response.data?.length || 0} groups`);
      return response.data;
    } catch (error) {
      console.error('walletApi.getWalletGroups ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch wallet groups');
    }
  },

  // Get wallet groups with bot status
  async getWalletGroupsWithStatus(tokenId = null) {
    console.log('walletApi.getWalletGroupsWithStatus: Fetching wallet groups with bot status, tokenId:', tokenId);
    try {
      let url = '/wallet-groups/status';
      if (tokenId) {
        url += `?tokenId=${encodeURIComponent(tokenId)}`;
      }
      const response = await api.get(url);
      console.log(`walletApi.getWalletGroupsWithStatus: Received ${response.data?.length || 0} groups with status`);
      return response.data;
    } catch (error) {
      console.error('walletApi.getWalletGroupsWithStatus ERROR:', error);
      // Fall back to regular wallet groups if status endpoint fails
      console.log('Falling back to regular wallet groups endpoint');
      return this.getWalletGroups();
    }
  },

  // Update a wallet group
  async updateWalletGroup(groupId, updateData) {
    console.log('walletApi.updateWalletGroup: Updating group:', groupId);
    try {
      const response = await api.put(`/wallet-groups/${groupId}`, updateData);
      console.log('walletApi.updateWalletGroup: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.updateWalletGroup ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to update wallet group');
    }
  },

  // Delete a wallet group
  async deleteWalletGroup(groupId) {
    console.log('walletApi.deleteWalletGroup: Deleting group:', groupId);
    try {
      const response = await api.delete(`/wallet-groups/${groupId}`);
      console.log('walletApi.deleteWalletGroup: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.deleteWalletGroup ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete wallet group');
    }
  },

  // Add wallets to a group
  async addWalletsToGroup(groupId, walletIds) {
    console.log(`walletApi.addWalletsToGroup: Adding ${walletIds.length} wallets to group:`, groupId);
    try {
      const response = await api.post(`/wallet-groups/${groupId}/wallets`, { walletIds: walletIds });
      console.log('walletApi.addWalletsToGroup: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.addWalletsToGroup ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to add wallets to group');
    }
  },

  // Remove wallets from a group
  async removeWalletsFromGroup(groupId, walletIds) {
    console.log(`walletApi.removeWalletsFromGroup: Removing ${walletIds.length} wallets from group:`, groupId);
    try {
      const response = await api.delete(`/wallet-groups/${groupId}/wallets`, { 
        data: { walletIds: walletIds } 
      });
      console.log('walletApi.removeWalletsFromGroup: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.removeWalletsFromGroup ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to remove wallets from group');
    }
  },
  
  // Get wallet balance and token balances
  async getWalletBalance(walletId) {
    console.log('walletApi.getWalletBalance: Fetching balance for wallet:', walletId);
    try {
      const response = await api.get(`/wallet/${walletId}/balance`);
      console.log('walletApi.getWalletBalance: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.getWalletBalance ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch wallet balance');
    }
  },

  // Get balances for multiple wallets at once
  async getMultipleWalletBalances(walletIds) {
    console.log('walletApi.getMultipleWalletBalances: Fetching balances for', walletIds.length, 'wallets');
    try {
      const response = await api.post('/wallet/balances', { walletIds });
      console.log('walletApi.getMultipleWalletBalances: Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('walletApi.getMultipleWalletBalances ERROR:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch wallet balances');
    }
  },

  // Cache wallets for offline use
  cacheWallets(wallets) {
    if (wallets && wallets.length > 0) {
      localStorage.setItem('cachedWallets', JSON.stringify(wallets));
      console.log(`Cached ${wallets.length} wallets for offline use`);
    }
  }
};

export default walletApi;